import React from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../../../utils';
import { Eye, Edit, Trash2, AlertTriangle } from 'lucide-react';

const punishmentLabels = {
  verbal_warning: 'Teguran Lisan',
  written_warning_1: 'SP 1',
  written_warning_2: 'SP 2',
  written_warning_3: 'SP 3',
  suspension: 'Skorsing',
  termination: 'Pemberhentian',
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  try {
    // Handle ISO date string (e.g., "2025-12-21T17:00:00.000Z")
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      // If it's not a valid date, try parsing as YYYY-MM-DD
      const parts = dateStr.split('T')[0].split('-');
      if (parts.length === 3) {
        const year = parseInt(parts[0]);
        const month = parseInt(parts[1]) - 1;
        const day = parseInt(parts[2]);
        const parsedDate = new Date(year, month, day);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate.toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          });
        }
      }
      return dateStr;
    }
    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch (error) {
    return dateStr;
  }
};

function LoadingSkeleton() {
  return (
    <div className="flex justify-center py-8">
      <div className="animate-spin w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full"></div>
    </div>
  );
}

function EmptyState() {
  return (
    <tr>
      <td colSpan={6} className="text-center py-8 text-gray-500">
        <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>Tidak ada data punishment</p>
      </td>
    </tr>
  );
}

function PunishmentRow({ item, getEmployeeName, onDelete }) {
  const employeeName = getEmployeeName(item.employee_id);

  return (
    <tr className="border-b hover:bg-gray-50">
      <td className="px-4 py-3 font-medium">{employeeName}</td>
      <td className="px-4 py-3">
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
          {punishmentLabels[item.punishment_type] || item.punishment_type}
        </span>
      </td>
      <td className="px-4 py-3 hidden md:table-cell">{item.violation_category}</td>
      <td className="px-4 py-3 hidden lg:table-cell">{formatDate(item.incident_date)}</td>
      <td className="px-4 py-3">
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            item.status === 'active'
              ? 'bg-yellow-100 text-yellow-700'
              : item.status === 'expired'
                ? 'bg-gray-100 text-gray-700'
                : 'bg-blue-100 text-blue-700'
          }`}
        >
          {item.status}
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        <div className="flex justify-center gap-2">
          <Link to={createPageUrl(`PunishmentForm?id=${item.id}&mode=view`)}>
            <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Lihat">
              <Eye className="w-4 h-4" />
            </button>
          </Link>
          <Link to={createPageUrl(`PunishmentForm?id=${item.id}`)}>
            <button
              className="p-2 hover:bg-green-100 rounded-lg transition-colors text-green-600"
              title="Edit"
            >
              <Edit className="w-4 h-4" />
            </button>
          </Link>
          <button
            onClick={() => onDelete(item.id, employeeName)}
            className="p-2 hover:bg-red-100 rounded-lg transition-colors text-red-600"
            title="Hapus"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function PunishmentTable({ punishments, loading, getEmployeeName, onDelete }) {
  return (
    <div className="overflow-x-auto">
      {loading ? (
        <LoadingSkeleton />
      ) : (
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Pegawai</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">
                Jenis Hukuman
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 hidden md:table-cell">
                Kategori
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 hidden lg:table-cell">
                Tanggal
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Status</th>
              <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {punishments.length === 0 ? (
              <EmptyState />
            ) : (
              punishments.map(item => (
                <PunishmentRow
                  key={item.id}
                  item={item}
                  getEmployeeName={getEmployeeName}
                  onDelete={onDelete}
                />
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
